// 게시판 상세 조회 모달 형식으로

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  ViewWeek as ViewWeekIcon,
  Refresh as RefreshIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import useCreate from "./useCreate";

interface CreateProps {
  open: boolean;
  onClose: () => void;
  comId: string;
  onSuccess?: () => void;
}

export const Create = ({ open, onClose, comId, onSuccess }: CreateProps) => {
  const {
    isLoading,
    isRefreshing,
    requestType,
    selectedPageId,
    pages,
    control,
    handleSubmit,
    onSubmitPage,
    onSubmitSection,
    handleClose,
    setRequestType,
    handlePageChange,
    handleRefreshPages,
  } = useCreate({ onClose, comId, onSuccess });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="request-dialog-title"
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle id="request-dialog-title">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" component="div" fontWeight={600}>
                새 자료 요청 작성
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <form>
              <Stack spacing={3} sx={{ mt: 1 }}>
                {/* 요청 유형 선택 */}
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 500, mb: 1 }}>
                    요청 유형
                  </FormLabel>
                  <RadioGroup
                    row
                    value={requestType}
                    onChange={(e) => setRequestType(parseInt(e.target.value))}
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <DashboardIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">페이지 자료</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ViewWeekIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">섹션 자료</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* 페이지 자료 선택 시 - 페이지 이름 직접 입력 */}
                {requestType === 1 && (
                  <Controller
                    name="categoryName"
                    control={control}
                    rules={{ required: "페이지 이름을 입력해주세요" }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth error={!!fieldState.error}>
                        <FormLabel required sx={{ fontWeight: 500, mb: 1 }}>
                          페이지 이름
                        </FormLabel>
                        <TextField
                          {...field}
                          size="small"
                          placeholder="새로운 페이지 이름을 입력하세요 (예: 공지사항, 갤러리 등)"
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message ||
                            "페이지 이름은 메뉴에 표시될 이름입니다."
                          }
                        />
                      </FormControl>
                    )}
                  />
                )}

                {/* 섹션 자료 선택 시 - 페이지 선택 후 섹션 정보 입력 */}
                {requestType === 2 && (
                  <>
                    <FormControl fullWidth>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <FormLabel required sx={{ fontWeight: 500 }}>
                          페이지 선택
                        </FormLabel>
                        <Tooltip title="페이지 목록 새로고침">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleRefreshPages}
                            sx={{ minWidth: "auto" }}
                            disabled={isRefreshing}
                          >
                            {isRefreshing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </Button>
                        </Tooltip>
                      </Box>

                      <Controller
                        name="parentMasterCategoryId"
                        control={control}
                        rules={{ required: "페이지를 선택해주세요" }}
                        render={({ fieldState }) => (
                          <>
                            <TextField
                              select
                              size="small"
                              error={!!fieldState.error}
                              helperText={
                                fieldState.error?.message ||
                                "섹션을 추가할 페이지를 선택해주세요"
                              }
                              value={selectedPageId}
                              onChange={(e) => handlePageChange(e.target.value)}
                              fullWidth
                            >
                              {pages.map((page) => (
                                <MenuItem key={page.value} value={page.value}>
                                  {page.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </>
                        )}
                      />
                    </FormControl>

                    {selectedPageId && (
                      <Controller
                        name="categoryName"
                        control={control}
                        rules={{ required: "섹션 이름을 입력해주세요" }}
                        render={({ field, fieldState }) => (
                          <FormControl fullWidth error={!!fieldState.error}>
                            <FormLabel required sx={{ fontWeight: 500, mb: 1 }}>
                              섹션 이름
                            </FormLabel>
                            <TextField
                              {...field}
                              size="small"
                              placeholder="새로운 섹션 이름을 입력하세요"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          </FormControl>
                        )}
                      />
                    )}
                  </>
                )}

                {/* 설명 (공통) */}
                <Controller
                  name="categoryDesc"
                  control={control}
                  rules={{ required: "설명을 입력해주세요" }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <FormLabel required sx={{ fontWeight: 500, mb: 1 }}>
                        {requestType === 1 ? "페이지 설명" : "섹션 설명"}
                      </FormLabel>
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        placeholder={
                          requestType === 1
                            ? "페이지에 대한 상세한 설명을 입력하세요"
                            : "섹션에 대한 상세한 설명을 입력하세요"
                        }
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    </FormControl>
                  )}
                />

                {/* 위치 설명 (선택적) */}
                <Controller
                  name="locationDesc"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                        위치 설명 (선택사항)
                      </FormLabel>
                      <TextField
                        {...field}
                        size="small"
                        placeholder={
                          requestType === 1
                            ? "페이지의 메뉴 위치 등 관련 정보를 입력하세요"
                            : "섹션이 배치될 위치에 대한 설명을 입력하세요 (예: 메인 페이지 상단)"
                        }
                      />
                      <FormHelperText>이 필드는 선택사항입니다.</FormHelperText>
                    </FormControl>
                  )}
                />

                {/* 데모 페이지 URL (선택적) */}
                <Controller
                  name="demoPageUrl"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                        참고 URL (선택사항)
                      </FormLabel>
                      <TextField
                        {...field}
                        size="small"
                        placeholder="참고할 수 있는 웹사이트 URL을 입력하세요"
                      />
                      <FormHelperText>
                        참고하고 싶은 웹사이트가 있다면 URL을 입력해주세요.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Stack>
            </form>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleClose} color="inherit">
              취소
            </Button>

            {requestType === 1 ? (
              // 페이지 요청 등록 버튼
              <Button
                onClick={handleSubmit(onSubmitPage)}
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                disabled={isLoading}
                color="primary"
              >
                페이지 요청 등록
              </Button>
            ) : (
              // 섹션 요청 등록 버튼
              <Button
                onClick={handleSubmit(onSubmitSection)}
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={isLoading || !selectedPageId}
                color="success"
              >
                섹션 요청 등록
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
